import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ApiService } from './api.service';

/** One entry of a play queue — only the fields the players and their cards render. */
export interface PlayQueueItem {
  slug: string;
  title?: string;
  thumbnail_image?: string;
  video_duration?: string;
  is_shorts?: any;
}

export interface PlayQueue {
  playlistId: string;
  playlistName: string;
  items: PlayQueueItem[];
  /** Play straight through regardless of the account's auto_play setting (ad-hoc
   * sequences such as "everything published after this one"). */
  autoPlay?: boolean;
}

/**
 * The playlist the user is currently playing through, shared by every player.
 *
 * A playlist can mix normal videos, YouTube/Vimeo embeds and shorts, and those play in
 * two different components — DrmPlayerComponent behind /watch, ShortsPlayerComponent
 * behind /shorts. Neither can own the sequence, so it lives here: the queue is written
 * to localStorage (it has to survive the component swap and a reload) and can be
 * rebuilt from the API when a /watch or /shorts URL is opened cold.
 *
 * The playlist id and name also travel in the URL, so a refresh or a shared link keeps
 * playing the same sequence instead of dropping back to "just this video".
 */
@Injectable({ providedIn: 'root' })
export class PlayQueueService {
  private static readonly STORAGE_KEY = 'playerPlaylistQueue';
  /** Last URL segment when a playlist has no name — an empty segment would not match
   * the :name route param and the context would be lost on the next hop. */
  private static readonly UNNAMED = 'playlist';

  constructor(private apiService: ApiService) {}

  isShort(item: any): boolean {
    return !!item && Number(item.is_shorts) === 1;
  }

  /**
   * Where an item plays. Shorts belong to the vertical feed, everything else (normal,
   * DRM, YouTube/Vimeo embeds) to the watch player — and the playlist context is carried
   * along so whichever player takes over still knows what comes next.
   */
  pathFor(item: any, playlistId?: string, playlistName?: string): string {
    if (!item || !item.slug) {
      return '';
    }
    const base = this.isShort(item) ? '/shorts/' : '/watch/';
    return base + item.slug + this.contextSuffix(playlistId, playlistName);
  }

  /** Detail page for an item, playlist context preserved. */
  detailPathFor(item: any, playlistId?: string, playlistName?: string): string {
    if (!item || !item.slug) {
      return '';
    }
    return '/video/' + item.slug + this.contextSuffix(playlistId, playlistName);
  }

  private contextSuffix(playlistId?: string, playlistName?: string): string {
    if (!playlistId) {
      return '';
    }
    const name = playlistName ? encodeURIComponent(playlistName) : PlayQueueService.UNNAMED;
    return '/' + playlistId + '/' + name;
  }

  indexOf(items: PlayQueueItem[], slug: string): number {
    if (!Array.isArray(items) || !slug) {
      return -1;
    }
    return items.findIndex(v => v && v.slug === slug);
  }

  nextOf(items: PlayQueueItem[], slug: string): PlayQueueItem {
    const index = this.indexOf(items, slug);
    if (index === -1) {
      // Not part of the queue (a list that excludes the video being played) — the
      // sequence starts at its first entry.
      return Array.isArray(items) && items.length ? items[0] : null;
    }
    return index + 1 < items.length ? items[index + 1] : null;
  }

  prevOf(items: PlayQueueItem[], slug: string): PlayQueueItem {
    const index = this.indexOf(items, slug);
    return index > 0 ? items[index - 1] : null;
  }

  read(): PlayQueue {
    try {
      const stored = localStorage.getItem(PlayQueueService.STORAGE_KEY);
      if (!stored) {
        return null;
      }
      const queue = JSON.parse(stored);
      if (!queue || !Array.isArray(queue.items) || !queue.items.length) {
        return null;
      }
      return queue;
    } catch (e) {
      console.warn('[play-queue] reading the stored queue failed', e);
      return null;
    }
  }

  clear() {
    try {
      localStorage.removeItem(PlayQueueService.STORAGE_KEY);
    } catch (e) {
      console.warn('[play-queue] clearing the stored queue failed', e);
    }
  }

  /**
   * Store the playlist as it is known right now (the detail page already has the list,
   * so no request is needed) and hand back the normalised queue.
   *
   * @param currentVideo the video the user is about to play — prepended when the list
   * does not contain it, so every consumer can rely on the current video having a
   * position in the queue.
   * @param autoPlay force auto-advance for this queue (see PlayQueue.autoPlay).
   */
  save(playlistId: string, playlistName: string, items: any[], currentVideo?: any, autoPlay?: boolean): PlayQueue {
    const queue = this.build(playlistId, playlistName, items, currentVideo);
    if (!queue) {
      return null;
    }
    if (autoPlay) {
      queue.autoPlay = true;
    }
    try {
      localStorage.setItem(PlayQueueService.STORAGE_KEY, JSON.stringify(queue));
    } catch (e) {
      console.warn('[play-queue] storing the queue failed', e);
    }
    return queue;
  }

  /**
   * The queue for a playlist, fetched only when the stored one cannot answer for this
   * video — a cold /watch or /shorts URL, a reload, or a video from a page of the
   * playlist that was never loaded. Emits null when there is nothing usable, so callers
   * can fall back instead of stalling.
   */
  ensure(playlistId: string, playlistName: string, currentSlug: string): Observable<PlayQueue> {
    if (!playlistId) {
      return of(null);
    }

    const cached = this.read();
    if (cached && cached.playlistId === playlistId && this.indexOf(cached.items, currentSlug) !== -1) {
      if (playlistName && cached.playlistName !== playlistName) {
        cached.playlistName = playlistName;
      }
      return of(cached);
    }

    if (!currentSlug) {
      return of(null);
    }

    // Same endpoint the detail page uses: for a playlist it answers with the playlist's
    // videos in order, in the shape the video cards already expect.
    return this.apiService.callGetAPI('v2/videos/' + currentSlug + '/' + playlistId).pipe(
      map(data => {
        const response = data && data['response'];
        const related = response && response.related && response.related.data;
        return this.save(playlistId, playlistName, related, response && response.video_info);
      }),
      catchError(error => {
        console.warn('[play-queue] could not load playlist', playlistId, error);
        return of(null);
      }));
  }

  private build(playlistId: string, playlistName: string, items: any[], currentVideo?: any): PlayQueue {
    const normalised = (Array.isArray(items) ? items : [])
      .filter(v => v && v.slug)
      .map(v => this.toItem(v));

    if (currentVideo && currentVideo.slug && this.indexOf(normalised, currentVideo.slug) === -1) {
      normalised.unshift(this.toItem(currentVideo));
    }

    if (!normalised.length || !playlistId) {
      return null;
    }
    return { playlistId, playlistName: playlistName || '', items: normalised };
  }

  private toItem(video: any): PlayQueueItem {
    return {
      slug: video.slug,
      title: video.title,
      thumbnail_image: video.thumbnail_image,
      // The detail page splits video_duration into an array for its own display; keep a
      // string so the up-next card can print it.
      video_duration: Array.isArray(video.video_duration)
        ? video.video_duration.join(':')
        : video.video_duration,
      is_shorts: video.is_shorts
    };
  }
}
